import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './RetentionPanel.css';

/**
 * Where people fall out.
 *
 * The stages narrow — saw it, granted it, used it, came back, still here — and
 * the losses beside them say which step lost whom. A funnel that only counts
 * successes tells you the number; this is meant to tell you the reason.
 *
 * One stage is younger than the others and says so. Nothing recorded that a
 * person had seen a consent screen until recently, so "saw it and did not
 * authorize" counts from that date rather than from the app's life. Presenting
 * it as though it reached back would make every app look like it converts
 * perfectly.
 */
function RetentionPanel({ appId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await api.request(`/third-party/${appId}/retention`);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load retention.');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [appId]);

  if (loading) return <p className="ret__msg">Loading…</p>;
  if (error) return <p className="ret__msg ret__msg--bad">{error}</p>;

  const stages = data?.stages || [];
  const losses = data?.losses || [];
  // The widest stage sets the scale. Using the first would misdraw the bars
  // whenever consent views are younger than the grants below them.
  const widest = Math.max(1, ...stages.map(s => s.count));
  const since = data?.consent_views_recorded_since;

  return (
    <div className="ret">
      <section>
        <h4>How far people get</h4>
        <div className="ret__funnel">
          {stages.map(s => (
            <div key={s.key} className="ret__stage">
              <div className="ret__stage-head">
                <span className="ret__stage-label">{s.label}</span>
                <span className="ret__stage-count">{s.count}</span>
              </div>
              <div className="ret__bar">
                <span style={{ width: `${Math.round((s.count / widest) * 100)}%` }} />
              </div>
              <p className="ret__note">{s.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h4>Where they went</h4>
        <p className="ret__lede">
          Each of these is a different problem, which is why they are counted apart.
        </p>
        <table className="ret__table">
          <tbody>
            {losses.map(l => (
              <tr key={l.key} className={l.count > 0 ? 'has-some' : ''}>
                <td className="ret__loss-count">{l.count}</td>
                <td className="ret__loss-label">{l.label}</td>
                <td className="ret__loss-note">{l.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className="ret__caveat">
        {since
          ? <>Consent screens have been recorded since {new Date(since).toLocaleDateString()}.
              Anyone who authorized before then is counted from “Authorized” on, so the first stage
              is not comparable with the rest until a full cycle has passed.</>
          : <>No consent screen has been recorded yet. That stage stays at zero until someone opens
              one — it does not mean nobody has looked.</>}
      </p>
    </div>
  );
}

export default RetentionPanel;
